import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import axios from 'axios';
import PublicToolsMenu from '../components/public/PublicToolsMenu';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// CSS defensivo por si el import del bundle no aplica en producción:
// forzamos altura del canvas y visibilidad
const DEFENSIVE_CSS = `
  .mapboxgl-map { position: relative; width: 100%; height: 100%; }
  .mapboxgl-canvas-container, .mapboxgl-canvas { width: 100% !important; height: 100% !important; }
  .mapboxgl-ctrl-geocoder { width: 100% !important; max-width: 100% !important; box-shadow: none !important; border: 1px solid #e2e8f0; }
  .mapboxgl-ctrl-geocoder input { padding-left: 40px !important; }
`;

// Sucursales — ajustar si abren más
// closedDay: 0=Domingo, 1=Lunes, 2=Martes... según Date.getDay()
const BRANCHES = [
  { id: 'america', name: 'Sucursal América', lng: -100.96496449312514, lat: 25.43860048729518, closedDay: 0, closedDayLabel: 'domingos' },
  { id: 'morelos', name: 'Sucursal Morelos', lng: -100.94424590860048, lat: 25.42114314472833, closedDay: 1, closedDayLabel: 'lunes' },
];

function isBranchClosedToday(b) {
  return typeof b?.closedDay === 'number' && new Date().getDay() === b.closedDay;
}

// Bounding box aproximado de Saltillo/Ramos Arizpe para limitar el autocomplete
// [westLng, southLat, eastLng, northLat]
const SEARCH_BBOX = [-101.15, 25.28, -100.78, 25.56];

export default function ShippingQuote() {
  const mapContainer = useRef(null);
  const geocoderContainer = useRef(null);
  const mapRef = useRef(null);
  const destinationMarkerRef = useRef(null);
  const branchMarkerRef = useRef(null);
  const geocoderRef = useRef(null);

  // Si la sucursal por defecto está cerrada hoy, arrancar con la otra
  const [selectedBranch, setSelectedBranch] = useState(() => {
    const open = BRANCHES.find((b) => !isBranchClosedToday(b));
    return open || BRANCHES[0];
  });
  const [destination, setDestination] = useState(null); // { lng, lat, colonia, placeName }
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null); // { km, price, source, ... }
  const [error, setError] = useState('');
  const [mapError, setMapError] = useState(''); // error específico del render del mapa

  // Inicializar el mapa una sola vez
  useEffect(() => {
    if (!MAPBOX_TOKEN) {
      setError('Falta REACT_APP_MAPBOX_TOKEN en la configuración');
      return;
    }
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [selectedBranch.lng, selectedBranch.lat],
      zoom: 12,
    });
    // Capturar errores del mapa y mostrarlos en pantalla
    map.on('error', (e) => {
      const err = e?.error;
      let msg = '';
      if (err) {
        if (err.status) msg = `HTTP ${err.status}`;
        if (err.message) msg += (msg ? ' — ' : '') + err.message;
        if (err.url) msg += ` (url: ${err.url})`;
      }
      msg = msg || 'Error desconocido cargando el mapa';
      console.error('[Mapbox error]', e);
      setMapError(msg);
    });
    map.addControl(new mapboxgl.NavigationControl({ visualizePitch: false }), 'top-right');
    mapRef.current = map;

    // Geocoder (autocomplete) montado en su contenedor propio
    const geocoder = new MapboxGeocoder({
      accessToken: MAPBOX_TOKEN,
      mapboxgl,
      language: 'es',
      countries: 'mx',
      bbox: SEARCH_BBOX,
      // Incluye direcciones (calles con número), colonias/barrios y localidades
      // para que el vendedor pueda buscar "Los Maestros" o "Nazario Ortiz 500"
      types: 'address,poi,neighborhood,locality',
      // Priorizar resultados cercanos a la sucursal seleccionada
      proximity: { longitude: selectedBranch.lng, latitude: selectedBranch.lat },
      limit: 8,
      minLength: 3,
      placeholder: 'Colonia y calle. Ej: Los Maestros, Nazario Ortiz 500',
      marker: false, // el marker lo pintamos nosotros
      // Custom render: mostrar SIEMPRE la colonia si viene en el contexto,
      // para que el vendedor pueda distinguir calles con el mismo nombre en
      // distintas colonias.
      render: (item) => {
        const escape = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
        const title = item.address
          ? `${item.text} ${item.address}`
          : item.text;
        let colonia = '';
        let city = '';
        let postcode = '';
        const pt = Array.isArray(item.place_type) ? item.place_type : [];
        // Si el resultado ES una colonia, no necesitamos leer context (ya es la colonia)
        if (pt.includes('neighborhood') || pt.includes('locality')) {
          colonia = item.text || '';
        }
        if (Array.isArray(item.context)) {
          for (const c of item.context) {
            const id = c.id || '';
            if (!colonia && (id.startsWith('neighborhood') || id.startsWith('locality'))) {
              colonia = c.text || '';
            }
            if (!city && (id.startsWith('place') || id.startsWith('district'))) {
              city = c.text || '';
            }
            if (!postcode && id.startsWith('postcode')) {
              postcode = c.text || '';
            }
          }
        }
        const sublineParts = [];
        if (colonia) sublineParts.push(`<strong style="color:#7c3aed">${escape(colonia)}</strong>`);
        if (postcode) sublineParts.push(escape(postcode));
        if (city) sublineParts.push(escape(city));
        const subline = sublineParts.join(' · ');
        return `
          <div style="padding:2px 0">
            <div style="font-weight:600;color:#0f172a">${escape(title)}</div>
            ${subline ? `<div style="font-size:12px;color:#64748b;margin-top:2px">${subline}</div>` : ''}
          </div>
        `;
      },
    });
    geocoderRef.current = geocoder;

    geocoder.on('result', (evt) => {
      const feat = evt.result;
      if (!feat?.geometry?.coordinates) return;
      const [lng, lat] = feat.geometry.coordinates;
      // Nombre principal (calle o POI). `text` es el nombre corto (ej "Nazario Ortiz 500")
      // y `address` a veces trae el número.
      const streetPart = feat.address
        ? `${feat.text} ${feat.address}`
        : (feat.text || feat.place_name || '');

      // Colonia: puede venir en `context[]` como neighborhood, locality o place.
      // Cuando la sugerencia elegida ES una colonia, viene como `feat.text` directamente
      // y su `place_type` incluye 'neighborhood' o 'locality'.
      let colonia = '';
      let city = '';
      const pt = Array.isArray(feat.place_type) ? feat.place_type : [];
      if (pt.includes('neighborhood') || pt.includes('locality')) {
        colonia = feat.text || '';
      }
      if (Array.isArray(feat.context)) {
        for (const c of feat.context) {
          const id = c.id || '';
          if (!colonia && (id.startsWith('neighborhood') || id.startsWith('locality'))) {
            colonia = c.text || '';
          }
          if (!city && (id.startsWith('place') || id.startsWith('district'))) {
            city = c.text || '';
          }
        }
      }
      setDestination({ lng, lat, colonia, city, street: streetPart, placeName: feat.place_name });
      setQuote(null);
      setError('');
    });

    if (geocoderContainer.current) {
      geocoderContainer.current.innerHTML = '';
      geocoderContainer.current.appendChild(geocoder.onAdd(map));
    }

    // Marker de la sucursal
    const branchEl = document.createElement('div');
    branchEl.className = 'branch-marker';
    branchEl.style.cssText = 'width:36px;height:36px;background:#dc2626;border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:16px;';
    branchEl.textContent = '🏪';
    branchMarkerRef.current = new mapboxgl.Marker({ element: branchEl })
      .setLngLat([selectedBranch.lng, selectedBranch.lat])
      .setPopup(new mapboxgl.Popup({ offset: 20 }).setText(selectedBranch.name))
      .addTo(map);

    return () => {
      try { map.remove(); } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reubicar marker de sucursal al cambiar el selector
  useEffect(() => {
    if (!mapRef.current || !branchMarkerRef.current) return;
    branchMarkerRef.current.setLngLat([selectedBranch.lng, selectedBranch.lat]);
    branchMarkerRef.current.getPopup().setText(selectedBranch.name);
    // Actualizar prioridad del autocomplete a la nueva sucursal
    if (geocoderRef.current?.setProximity) {
      geocoderRef.current.setProximity({ longitude: selectedBranch.lng, latitude: selectedBranch.lat });
    }
    // Recalcular ruta si hay destino
    if (destination) drawRouteAndQuote();
    else mapRef.current.flyTo({ center: [selectedBranch.lng, selectedBranch.lat], zoom: 12 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch]);

  // Al cambiar destino, dibujar y cotizar
  useEffect(() => {
    if (destination) drawRouteAndQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination]);

  const drawRouteAndQuote = async () => {
    if (!mapRef.current || !destination) return;
    setLoading(true);
    setError('');
    try {
      // 1) Actualizar marker de destino
      if (destinationMarkerRef.current) destinationMarkerRef.current.remove();
      const destEl = document.createElement('div');
      destEl.style.cssText = 'width:34px;height:34px;background:#2563eb;border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-size:14px;';
      destEl.textContent = '📍';
      destinationMarkerRef.current = new mapboxgl.Marker({ element: destEl })
        .setLngLat([destination.lng, destination.lat])
        .addTo(mapRef.current);

      // 2) Llamar Directions API de Mapbox para calcular ruta por carretera
      const url =
        `https://api.mapbox.com/directions/v5/mapbox/driving/` +
        `${selectedBranch.lng},${selectedBranch.lat};${destination.lng},${destination.lat}` +
        `?geometries=geojson&overview=full&access_token=${MAPBOX_TOKEN}`;
      const dirRes = await fetch(url);
      const dirData = await dirRes.json();
      if (!dirData?.routes?.[0]) {
        throw new Error('No se pudo trazar la ruta a esa dirección');
      }
      const route = dirData.routes[0];
      // Redondear km SIEMPRE hacia arriba (el vendedor cobra completo)
      const km = Math.ceil(route.distance / 1000);

      // 3) Pintar la ruta
      const map = mapRef.current;
      const geojson = { type: 'Feature', properties: {}, geometry: route.geometry };
      if (map.getSource('route')) {
        map.getSource('route').setData(geojson);
      } else {
        map.addSource('route', { type: 'geojson', data: geojson });
        map.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { 'line-color': '#2563eb', 'line-width': 4, 'line-opacity': 0.75 },
        });
      }
      // Ajustar zoom a la ruta
      const bounds = new mapboxgl.LngLatBounds()
        .extend([selectedBranch.lng, selectedBranch.lat])
        .extend([destination.lng, destination.lat]);
      map.fitBounds(bounds, { padding: 80, maxZoom: 15, duration: 800 });

      // 4) Cotizar contra el backend
      const { data } = await axios.post(`${API}/public/shipping-quote`, {
        km,
        colonia: destination.colonia || '',
      });
      setQuote(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Error al cotizar');
      setQuote(null);
    } finally {
      setLoading(false);
    }
  };

  const clearAll = () => {
    setDestination(null);
    setQuote(null);
    setError('');
    if (destinationMarkerRef.current) { destinationMarkerRef.current.remove(); destinationMarkerRef.current = null; }
    if (mapRef.current?.getLayer('route')) mapRef.current.removeLayer('route');
    if (mapRef.current?.getSource('route')) mapRef.current.removeSource('route');
    if (geocoderRef.current) { try { geocoderRef.current.clear(); } catch { /* ignore */ } }
    if (mapRef.current) mapRef.current.flyTo({ center: [selectedBranch.lng, selectedBranch.lat], zoom: 12 });
  };

  const priceFmt = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n || 0));

  return (
    <div className="min-h-screen bg-slate-50">
      <style>{DEFENSIVE_CSS}</style>
      <PublicToolsMenu current="/cotizador" />
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 shadow-sm" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between flex-wrap gap-3 pl-14">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-slate-800">🚚 Cotizador de Envíos</h1>
            <p className="text-xs text-slate-500">Escribe la dirección del cliente y te doy el costo</p>
          </div>
          {/* Selector de sucursal */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-500">Sucursal:</span>
            {BRANCHES.map((b) => {
              const closed = isBranchClosedToday(b);
              const active = selectedBranch.id === b.id;
              return (
                <button
                  key={b.id}
                  onClick={() => {
                    if (closed) {
                      alert(`🚫 ${b.name} cierra los ${b.closedDayLabel}. Elige la otra sucursal.`);
                      return;
                    }
                    setSelectedBranch(b);
                  }}
                  disabled={closed}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium btn-transition relative ${
                    closed
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed line-through'
                      : active
                        ? 'bg-red-600 text-white shadow'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  data-testid={`branch-${b.id}`}
                  title={closed ? `Cerrado los ${b.closedDayLabel}` : b.name}
                >
                  {b.name.replace('Sucursal ', '')}
                  {closed && <span className="ml-1">🚫</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Aviso banner si la sucursal seleccionada estuviera cerrada (edge case) */}
      {isBranchClosedToday(selectedBranch) && (
        <div className="bg-amber-100 border-b border-amber-300 text-amber-900 px-4 py-2 text-sm text-center" data-testid="branch-closed-banner">
          🚫 {selectedBranch.name} está cerrada hoy ({selectedBranch.closedDayLabel}). Cambia a la otra sucursal para cotizar.
        </div>
      )}

      <div className="max-w-6xl mx-auto p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Mapa + geocoder */}
        <div className="order-2 lg:order-1 lg:col-span-2 bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm">
          <div className="p-3 border-b border-slate-100" ref={geocoderContainer} data-testid="quote-geocoder" />
          <div ref={mapContainer} style={{ width: '100%', height: '520px', position: 'relative', background: '#f1f5f9' }} data-testid="quote-map">
            {!MAPBOX_TOKEN && (
              <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
                <div className="bg-white border-2 border-rose-300 rounded p-4 max-w-sm shadow">
                  <p className="text-rose-700 font-semibold mb-1">⚠️ Falta configurar Mapbox</p>
                  <p className="text-xs text-slate-600">
                    La variable <code>REACT_APP_MAPBOX_TOKEN</code> no está definida en este entorno.
                    Redespliega la app o pídele al admin que la agregue.
                  </p>
                </div>
              </div>
            )}
            {mapError && (
              <div className="absolute bottom-2 left-2 right-2 bg-rose-600 text-white text-xs rounded p-2 shadow-lg z-10" data-testid="map-error">
                <p className="font-semibold mb-0.5">⚠️ Error de Mapbox:</p>
                <p className="opacity-90 break-all">{mapError}</p>
              </div>
            )}
          </div>
        </div>

        {/* Panel de resultado (arriba en móvil para que los vendedores lo vean sin scroll, a la derecha en desktop) */}
        <div className="order-1 lg:order-2 bg-white rounded-lg border border-slate-200 shadow-sm p-4 flex flex-col gap-4">
          {error && (
            <div className="bg-rose-50 text-rose-700 border border-rose-200 rounded p-3 text-sm">
              {error}
            </div>
          )}
          {!destination && !error && (
            <div className="text-sm text-slate-500 border border-dashed border-slate-300 rounded p-4 text-center">
              👆 Escribe una dirección arriba para cotizar
            </div>
          )}
          {destination && (
            <>
              <div className="space-y-1.5">
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Calle / lugar</p>
                    <button
                      onClick={clearAll}
                      className="w-6 h-6 rounded-full bg-slate-100 hover:bg-rose-100 hover:text-rose-600 text-slate-500 flex items-center justify-center text-sm font-bold btn-transition"
                      title="Borrar y cotizar otra dirección"
                      data-testid="quote-reset"
                    >
                      ×
                    </button>
                  </div>
                  <p className="text-sm text-slate-800 font-medium mt-0.5" data-testid="quote-street">
                    {destination.street || destination.placeName}
                  </p>
                </div>
                {destination.colonia && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Colonia</p>
                    <p className="text-sm text-slate-700 mt-0.5" data-testid="quote-colonia">{destination.colonia}</p>
                  </div>
                )}
                {destination.city && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Ciudad</p>
                    <p className="text-sm text-slate-500 mt-0.5">{destination.city}</p>
                  </div>
                )}
              </div>

              {loading ? (
                <div className="text-sm text-slate-500 flex items-center gap-2">
                  <span className="inline-block w-4 h-4 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
                  Calculando ruta y precio…
                </div>
              ) : quote ? (
                <>
                  {/* Km */}
                  <div className="bg-blue-50 border border-blue-200 rounded p-3 flex items-center justify-between">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-blue-600 font-semibold">Distancia por carretera</p>
                      <p className="text-2xl font-bold text-blue-800 mt-0.5" data-testid="quote-km">{quote.km} km</p>
                    </div>
                    <span className="text-3xl">🗺️</span>
                  </div>

                  {/* Precio */}
                  <div className={`rounded p-4 border ${quote.offer?.active ? 'bg-rose-50 border-rose-300' : 'bg-emerald-50 border-emerald-200'}`}>
                    <p className={`text-[11px] uppercase tracking-wide font-semibold ${quote.offer?.active ? 'text-rose-600' : 'text-emerald-600'}`}>
                      Cobrar al cliente
                    </p>
                    {quote.price !== null && quote.price !== undefined ? (
                      <div className="mt-1">
                        {quote.offer?.active && quote.price_original !== null && quote.price_original !== undefined && quote.price_original > quote.price && (
                          <p className="text-lg text-slate-500 line-through leading-tight" data-testid="quote-price-original">
                            {priceFmt(quote.price_original)}
                          </p>
                        )}
                        <p className={`text-4xl font-extrabold leading-tight ${quote.offer?.active ? 'text-rose-700' : 'text-emerald-700'}`} data-testid="quote-price">
                          {priceFmt(quote.price)}
                        </p>
                        {quote.offer?.active && (
                          <div className="mt-2 inline-flex items-center gap-1.5 bg-rose-600 text-white px-2.5 py-1 rounded-full text-xs font-bold shadow" data-testid="quote-offer-badge">
                            <span>🎁</span>
                            <span>
                              {quote.offer?.badge_text
                                || `Envío -${Math.round(quote.offer?.discount_pct || 0)}%${quote.offer?.end_date ? ` · hasta ${quote.offer.end_date}` : ''}`}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-600 mt-1 font-medium">
                        📞 Cotizar directamente con tienda
                      </p>
                    )}
                    {quote.source && !quote.offer?.active && (
                      <p className="text-[11px] text-emerald-700 mt-1">
                        {quote.source === 'formula' ? '📐 Precio calculado por fórmula' : '📋 Precio manual configurado'}
                      </p>
                    )}
                  </div>

                  {/* Desglose */}
                  <div className="border border-slate-200 rounded p-3 text-xs text-slate-600 space-y-1">
                    <p className="font-semibold text-slate-700 mb-1">Desglose</p>
                    {quote.colonia_matched && (
                      <p>📍 Colonia detectada: <span className="font-medium">{quote.colonia_matched}</span></p>
                    )}
                    {quote.formula_price !== null && quote.formula_price !== undefined && (
                      <p>Fórmula: {priceFmt(quote.formula_price)}</p>
                    )}
                    {quote.manual_price !== null && quote.manual_price !== undefined && (
                      <p>Manual: {priceFmt(quote.manual_price)}</p>
                    )}
                  </div>
                </>
              ) : null}
            </>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 pb-4 text-[11px] text-slate-400 text-center">
        Sistema POS · Cotizador público
      </div>
    </div>
  );
}
